import React from "react";
import { injectIntl, IntlShape } from "react-intl";
import { match as Match } from "react-router-dom";
import Table from "../../Table";
import IntlMessages from "../../../utils/messages";
import { Center } from "../../../lib/ground-aws-graphql-core/models/Center";
import { Quote } from "../../../lib/ground-aws-graphql-core/models/Api/Quotes";
import { getFormattedPrice, getUnitLabel } from "../../../utils/price-unit";
import { getTranslation } from "../../../utils/translation";
import { getBookingInBuildingTZ } from "../../../utils/config";

interface Props {
  center: Center | null;
  quote: Quote;
  intl: IntlShape;
  match?: Match<{ cid: string; id: string }>;
}

const GridQuoteItems = (props: Props) => {
  const { intl, center, quote } = props;

  const quoteItemsTableHead = [
    "page.quote.table.head.product",
    "page.quote.table.head.price.unit",
    "general.quantity",
    "general.total.ht",
    "general.vat",
    "general.total.ttc",
  ];

  const quoteItemsTableBody = quote.items?.map(item => {
    const {
      product,
      booking,
      price_unit,
      quantity,
      total_price,
      total_tax,
      total_price_tax,
      unit_price,
      currency,
      is_option,
    } = item;

    let unavailability;
    if (booking && center) {
      unavailability = getBookingInBuildingTZ(
        {
          start: booking.start_date,
          end: booking.end_date,
        },
        center.timezone
      );
    }

    return {
      id: item.id,
      rowElements: [
        {
          element: (
            <div className="flex flex-col items-start">
              <span>
                {getTranslation(product.name)}
                {is_option && (
                  <span className="text-ground-gray-100">
                    {" "}
                    (<IntlMessages id="page.list.products.badge.option" />)
                  </span>
                )}
              </span>

              {unavailability && unavailability.sameDay && (
                <div className="flex flex-col text-ground-gray-100">
                  <div className="flex flex-col">
                    <span>
                      <IntlMessages id="general.from" />{" "}
                      {unavailability.startDay} <IntlMessages id="general.at" />{" "}
                      {unavailability.startHour}
                    </span>
                    <span>
                      <IntlMessages id="general.to" /> {unavailability.endDay}{" "}
                      <IntlMessages id="general.at" /> {unavailability.endHour}
                    </span>
                  </div>
                  <div>
                    <IntlMessages
                      id="page.detail.quote.request.selections.booking.persons"
                      values={{
                        total: booking?.total_participants,
                      }}
                    />
                  </div>
                </div>
              )}

              {unavailability && !unavailability.sameDay && (
                <div className="flex flex-col text-ground-gray-100">
                  <div>
                    {unavailability.startDay} - {unavailability.startHour}
                    {" > "}
                    {unavailability.endHour}
                  </div>
                  <div>
                    <IntlMessages
                      id="page.detail.quote.request.selections.booking.persons"
                      values={{
                        total: booking?.total_participants,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ),
        },
        {
          element: (
            <span>
              {getFormattedPrice(unit_price, currency)}{" "}
              <IntlMessages id="general.ht" />
            </span>
          ),
        },
        {
          element: (
            <span>
              {quantity} {getUnitLabel(intl, price_unit.unit)}
            </span>
          ),
        },
        {
          element:
            total_price > 0 ? getFormattedPrice(total_price, currency) : null,
        },
        {
          element:
            total_tax > 0 ? getFormattedPrice(total_tax, currency) : null,
        },
        {
          element:
            total_price_tax > 0 ? (
              getFormattedPrice(total_price_tax, currency)
            ) : (
              <IntlMessages id="general.free" />
            ),
        },
      ],
    };
  });

  return (
    <Table
      head={quoteItemsTableHead}
      body={quoteItemsTableBody}
      noDataText=""
    />
  );
};

export default injectIntl(GridQuoteItems);
